/**
 * Display constants for the Document Review column, kept apart from the cell
 * component so react-refresh sees a component-only file.
 */
import { StatusChipColor } from "components/StatusChip";
import { ExtractionStatus } from "generated-graphql/graphql";

/** Just the fields the state is derived from. */
export interface DocumentStateRow {
  sourceDocument?: {
    extractionStatus?: ExtractionStatus | null;
    documentMetadata?: { needsReview: boolean; reviewedAt?: string | null } | null;
  } | null;
}

export const DOCUMENT_STATES = [
  "needsReview",
  "failed",
  "notProcessed",
  "processing",
  "approved",
  "none",
] as const;
export type DocumentState = (typeof DOCUMENT_STATES)[number];

export const DOCUMENT_STATE_LABELS: Record<DocumentState, string> = {
  needsReview: "Needs review",
  failed: "Failed",
  notProcessed: "Not processed",
  processing: "Processing",
  approved: "Approved",
  none: "No Document",
};

/** StatusChip color per document state, matching the Manifest Review datagrid's chip palette. */
export const DOCUMENT_STATE_CHIP_COLOR: Record<DocumentState, StatusChipColor> = {
  needsReview: "warning",
  failed: "error",
  notProcessed: "default",
  processing: "info",
  approved: "success",
  none: "default",
};

/**
 * Hover copy answering what the status means and, for the actionable states
 * (needsReview / failed / notProcessed / none), what clicking the chip does
 * about it.
 *
 * None of it names Encamp as the reviewer: this column is customer-facing, and
 * a customer reviews their own manifest documents.
 */
export const DOCUMENT_STATE_TOOLTIP: Record<DocumentState, string> = {
  // No promise about a queue: only the merged Shipment Documents grid opens one.
  // V2's chip lands on the drawer, so a shared tooltip cannot claim otherwise.
  needsReview: "Document data was extracted and is waiting to be reviewed. Click to review it.",
  notProcessed:
    "A document is attached but its data hasn't been processed. Click to open this manifest's documents and process it.",
  processing: "Document data is being extracted. Check back shortly.",
  approved: "Document data has been reviewed and approved.",
  failed:
    "Data extraction failed. Click to open this manifest's documents and re-process or upload a better copy.",
  none: "No source document is attached to this manifest. Click to open documents and upload one.",
};

/**
 * Tooltip variant for non-interactive renders (no onClick wired): same state
 * descriptions minus the "Click to…" call-to-action sentences.
 */
export const DOCUMENT_STATE_TOOLTIP_STATIC: Record<DocumentState, string> = {
  needsReview: "Document data was extracted and is waiting to be reviewed.",
  notProcessed: "A document is attached but its data hasn't been processed.",
  processing: "Document data is being extracted. Check back shortly.",
  approved: "Document data has been reviewed and approved.",
  failed: "Data extraction failed.",
  none: "No source document is attached to this manifest.",
};

/**
 * The Document Review state for a manifest row.
 *
 * Takes the shape it reads rather than the grid's `Row`, so this file stays free
 * of a component import — the reason it exists apart from the cell in the first
 * place — and so both manifest grids and the merged paperwork grid can call it.
 */
export function getDocumentState(row: DocumentStateRow): DocumentState {
  const sourceDocument = row.sourceDocument;
  if (!sourceDocument) return "none";
  if (sourceDocument.extractionStatus === ExtractionStatus.Failed) return "failed";
  const metadata = sourceDocument.documentMetadata;
  if (metadata?.needsReview) return "needsReview";
  // "approved" (reviewed) requires an actual human review, not just a
  // successful extraction — match the canonical isManifestDocumentReviewed rule
  // (needsReview === false AND reviewedAt != null).
  if (metadata?.needsReview === false && metadata.reviewedAt != null) return "approved";
  // Extraction in flight, or done and awaiting metadata promotion — mirrors
  // the DocumentsSection Process-gate reading of PENDING/SUCCEEDED.
  if (
    sourceDocument.extractionStatus === ExtractionStatus.Pending ||
    sourceDocument.extractionStatus === ExtractionStatus.Succeeded
  ) {
    return "processing";
  }
  // NOT_ATTEMPTED (the schema default) or null: nothing has been attempted.
  return "notProcessed";
}
